import { trpc } from '@/utils/trpc-client';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import { Button } from '@/components/display/button';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import { useIntercom } from 'react-use-intercom';
import { IntercomIcon } from '@/components/display/intercom-icon';
import { faArrowDown, faCircleQuestion } from '@fortawesome/pro-light-svg-icons';
import { useJiraSupportEnabled } from '@/utils/feature-flags';
import { reportAProblemUrl } from '@/utils/support-url';
import { useProposalContext } from '@/features/proposals/proposal-context';
import {
  hasBlockedDeletions,
  hasUnpricedLineItems
} from '@/features/proposals/promotion/proposal-diff-utils';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store/store';

type SubmitSectionProps = {
  setOpen: (open: boolean) => unknown;
};

type FormData = {
  comment: string;
};

export function SubmitSection({ setOpen }: SubmitSectionProps) {
  const { proposal, lineItems, diff } = useProposalContext();
  const isBlocked = hasBlockedDeletions(diff) || hasUnpricedLineItems(lineItems);
  const setDraftRevisions = useStore(state => state.setDraftRevisions);
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { mutateAsync, isPending, isError, error } = trpc.promoteProposal.useMutation({
    onSuccess: async () => {
      await utils.campaignV2.invalidate();
      await utils.getProposal.invalidate();
      await utils.getProposalLineItems.invalidate();
      await utils.cubeLoad.invalidate();
      setDraftRevisions(proposal.campaign_id, false);
      setOpen(false);
      navigate(`/campaigns/${proposal.campaign_id}/execution`);
    }
  });
  const { register, handleSubmit, formState } = useForm<FormData>();
  const { errors } = formState;

  async function publishChanges(data: FormData) {
    await mutateAsync({
      proposal_id: proposal.id,
      description: data.comment
    });
  }

  return (
    <div className="">
      <div className="h-[1px] w-full bg-gray-200" />
      <form onSubmit={handleSubmit(publishChanges)} className="flex flex-col gap-4 px-8 py-4">
        <div className="flex flex-col gap-2">
          <div className="text-sm text-gray-600">Comment</div>
          <textarea
            {...register('comment', { required: true })}
            placeholder="Comment on your changes"
            className={clsx(
              'w-full resize-none rounded-md px-4 py-3 font-light placeholder:text-gray-400',
              errors.comment ? 'border-red-400' : 'border-gray-300'
            )}
            rows={1}
          />
          <div className={clsx('text-sm', errors.comment ? 'text-red-500' : 'text-gray-500')}>
            Required
          </div>
        </div>
        <Hr />
        {isError && error?.data?.code !== 'CONFLICT' && <PublishError />}
        {isError && error?.data?.code === 'CONFLICT' && <ConflictError message={error.message} />}
        <Button
          className="w-full justify-around py-3"
          disabled={isPending || isBlocked}
          type="submit">
          {isPending ? 'Setting Execution Plan' : 'Set Execution Plan'}
          {isPending && (
            <motion.div
              animate={{ rotate: 360 }}
              className="ml-2"
              transition={{
                ease: 'linear',
                duration: 2,
                repeat: Infinity
              }}>
              <FontAwesomeIcon icon={faSpinner} />
            </motion.div>
          )}
        </Button>
      </form>
    </div>
  );
}

function PublishError() {
  const { showNewMessage: showNewIntercomMessage } = useIntercom();
  const jiraSupportEnabled = useJiraSupportEnabled();

  return (
    <div className="flex gap-4 rounded-sm bg-red-600 p-2 pl-4">
      <div className="flex flex-col gap-1 text-white">
        <div className="text-lg font-semibold">Publish failed.</div>
        <div className="text-sm">
          <div>Your work isn’t lost. It’s stored on your computer until it’s Published.</div>
          <div className="font-semibold">You can try again, or...</div>
        </div>
      </div>
      <div className="flex basis-[300px] gap-3 rounded-sm bg-white py-2 pl-4 pr-2">
        <div className="flex items-center text-sm text-gray-700">
          {jiraSupportEnabled
            ? 'If the issue continues, submit a support request.'
            : 'If the issue continues, send a message using Intercom at the bottom right of your screen.'}
        </div>
        {jiraSupportEnabled ? (
          <a
            href={reportAProblemUrl('Publish failed.')}
            target="_blank"
            rel="noreferrer"
            className="flex cursor-pointer flex-col items-center justify-between rounded-sm rounded-t-3xl bg-[#0171B2] p-2 pt-3 no-underline">
            <FontAwesomeIcon color="white" icon={faCircleQuestion} className="h-6 w-6" />
            <FontAwesomeIcon color="white" icon={faArrowDown} className="h-6 w-6" />
          </a>
        ) : (
          <div
            onClick={() => showNewIntercomMessage()}
            className="flex cursor-pointer flex-col items-center justify-between rounded-sm rounded-t-3xl bg-[#0171B2] p-2 pt-3">
            <IntercomIcon />
            <FontAwesomeIcon color="white" icon={faArrowDown} className="h-6 w-6" />
          </div>
        )}
      </div>
    </div>
  );
}

function ConflictError({ message }: { message: string }) {
  return (
    <div className="flex gap-4 rounded-sm bg-red-600 p-2 pl-4">
      <div className="flex flex-col gap-1 text-white">
        <div className="text-lg font-semibold">Publish failed.</div>
        <div className="whitespace-pre-wrap break-words text-sm">
          <div>{message.trim()}</div>
        </div>
      </div>
    </div>
  );
}

function Hr() {
  return <hr className="h-[2px] rounded-sm border-0 bg-gray-200" />;
}
